import {
  handleProjectInviteAccept,
  handleProjectInviteDecline,
  handleProjectLaunchedClick,
  handleLikeNotificationClick,
  handleCommentNotificationClick,
  handleSolutionVoteClick,
  handleFollowNotificationClick,
  handleFollowRequestAccept,
  handleFollowRequestReject,
  handleMessageNotificationClick
} from './handlers/index.js';
import { markNotificationAsRead, getProjectData, getUserData } from './utils.js';

/**
 * Service to handle notification actions and their follow-up behaviors
 */
class NotificationActionHandler {
  async handleProjectInviteAccept(inviteId, notification, navigate) {
    return handleProjectInviteAccept(inviteId, notification, navigate);
  }

  async handleProjectInviteDecline(inviteId, notification) {
    return handleProjectInviteDecline(inviteId, notification);
  }

  async handleLikeNotificationClick(notification, navigate) {
    return handleLikeNotificationClick(notification, navigate);
  }

  async handleCommentNotificationClick(notification, navigate) {
    return handleCommentNotificationClick(notification, navigate);
  }

  async handleFollowNotificationClick(notification, navigate, additionalData = {}) {
    return handleFollowNotificationClick(notification, navigate, additionalData);
  }

  async handleMessageNotificationClick(notification, navigate) {
    return handleMessageNotificationClick(notification, navigate);
  }

  async handleProjectLaunchedClick(notification, navigate) {
    return handleProjectLaunchedClick(notification, navigate);
  }

  async handleSolutionVoteClick(notification, navigate) {
    return handleSolutionVoteClick(notification, navigate);
  }

  async handleFollowRequestAccept(notification, currentUserId) {
    return handleFollowRequestAccept(notification, currentUserId);
  }

  async handleFollowRequestReject(notification, currentUserId) {
    return handleFollowRequestReject(notification, currentUserId);
  }

  /**
   * Generic notification click handler - routes to specific handlers
   */
  async handleNotificationClick(notification, navigate, additionalData = {}) {
    const { type } = notification;

    switch (type) {
      case 'like':
        return await this.handleLikeNotificationClick(notification, navigate);

      case 'comment':
      case 'reply':
        return await this.handleCommentNotificationClick(notification, navigate);

      case 'follow':
        return await this.handleFollowNotificationClick(notification, navigate, additionalData);

      case 'follow_request': {
        // Do not auto-accept; show options via UI
        await this.markNotificationAsRead(notification.id);
        return { success: true, requiresAction: true };
      }

      case 'follow_request_accepted':
        // Behave like a follow: navigate to the actor profile
        return await this.handleFollowNotificationClick(notification, navigate, additionalData);

      case 'message':
        return await this.handleMessageNotificationClick(notification, navigate);

      case 'project_launched':
        return await this.handleProjectLaunchedClick(notification, navigate);

      case 'solution_vote':
      case 'solution_reason':
        return await this.handleSolutionVoteClick(notification, navigate);

      case 'project_invite':
        // For project invites, don't auto-accept, just mark as read and show options
        await this.markNotificationAsRead(notification.id);
        return { success: true, requiresAction: true };

      case 'share':
        return await this.handleLikeNotificationClick(notification, navigate); // Same as like

      default:
        // Generic handler - just mark as read and navigate if URL exists
        try {
          await this.markNotificationAsRead(notification.id);

          if (notification.actionUrl) {
            // If URL points to a profile and it's the current user's, route to /profile
            const currentUserId = additionalData.currentUserId || null;
            const m = String(notification.actionUrl).match(/^\/profile\/(.+)$/);
            if (m && currentUserId && m[1] === currentUserId) {
              navigate('/profile');
            } else {
              navigate(notification.actionUrl);
            }
          } else {
            navigate('/home');
          }

          return { success: true };
        } catch (error) {
          return { success: false, error };
        }
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId) {
    return markNotificationAsRead(notificationId);
  }

  /**
   * Get project data for better UX
   */
  async getProjectData(projectId) {
    return getProjectData(projectId);
  }

  /**
   * Get user data for better UX
   */
  async getUserData(userId) {
    return getUserData(userId);
  }

  /**
   * Handle multiple notification actions (for bulk operations)
   */
  async handleBulkNotificationActions(notifications, action, navigate) {
    const results = [];

    for (const notification of notifications) {
      try {
        let result;

        switch (action) {
          case 'markAsRead':
            await this.markNotificationAsRead(notification.id);
            result = { success: true, id: notification.id };
            break;

          case 'click':
            result = await this.handleNotificationClick(notification, navigate);
            result.id = notification.id;
            break;

          default:
            result = { success: false, error: 'Unknown action', id: notification.id };
        }

        results.push(result);
      } catch (error) {
        results.push({ success: false, error, id: notification.id });
      }
    }

    return results;
  }
}

// Export singleton instance with name
const notificationActionHandler = new NotificationActionHandler();
export default notificationActionHandler;
